import React, { useState, useRef, useEffect } from "react"
import { QRCodeSVG } from "qrcode.react"
import { scanQRCode } from "../Util/scanQRCode"
import QRMessage from "./QRMessage"
import { useAuth0 } from "@auth0/auth0-react"
import "./EmpleadoPage.css"
import Spinner from "../Spinner/Spinner"
import SelectorEventosModal from "./SelectorEventosModal/SelectorEventosModal"
import ConteoIngresos from "./ConteoIngresos/ConteoIngresos"
import axios from "axios"
import { sortEventosByDate } from "../../utils/dateUtils"
import { capitalizeNombresPropios } from "../../utils/StringUtils"
import { toast } from "react-toastify"

const EmpleadoPage = () => {
  const [dni, setDni] = useState<string>("")
  const [clave, setClave] = useState<string>("")
  const [esMenor, setEsMenor] = useState("")
  const [isIdBased, setIsIdBased] = useState(false)
  const [qrData, setQrData] = useState("")
  const [cameraStarted, setCameraStarted] = useState(false)
  const [showNoCameraAccessMessage, setShowNoCameraAccessMessage] =
    useState(false)
  const [camerasAvailable, setCamerasAvailable] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [showMessage, setShowMessage] = useState(false)
  const [messageText, setMessageText] = useState("")
  const [tarjetasPorIngresar, setTarjetasPorIngresar] = useState("")
  const [observaciones, setObservaciones] = useState("")
  const [messageVariant, setMessageVariant] = useState("danger")
  const [mostrarMesa, setMostrarMesa] = useState(false)
  const [autoRestartCamera, setAutoRestartCamera] = useState(false)
  const API_URL = process.env.REACT_APP_API_SERVER_URL_EGRE_VERIF || ""
  const API_URL_MENOR =
    process.env.REACT_APP_API_SERVER_URL_EGRE_VERIF_MENORES || ""
  const API_URL_BAILE =
    process.env.REACT_APP_API_SERVER_URL_EGRE_VERIF_BAILE || ""
  const API_URL_ID = process.env.REACT_APP_API_SERVER_URL_EGRE_VERIF_ID || ""
  const API_URL_MENOR_ID =
    process.env.REACT_APP_API_SERVER_URL_EGRE_VERIF_MENORES_ID || ""
  const API_URL_BAILE_ID =
    process.env.REACT_APP_API_SERVER_URL_EGRE_VERIF_BAILE_ID || ""
  const { getAccessTokenSilently } = useAuth0()
  const [loading, setLoading] = useState(false)
  const [showEventModal, setShowEventModal] = useState(false)
  const [showConteoModal, setShowConteoModal] = useState(false)
  const [eventId, setEventId] = useState<number | null>(null)
  const API_URL_EVENTOS = process.env.REACT_APP_API_SERVER_URL_EVENTOS || ""

  const fetchEventos = async () => {
    setLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const response = await axios.get(API_URL_EVENTOS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const eventosOrdenados = sortEventosByDate(response.data)
      setEventId(eventosOrdenados[0].id)
    } catch (error) {
      console.error("Error al obtener eventos:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const cameraStateFromLocalStorage = localStorage.getItem("cameraStarted")
    if (cameraStateFromLocalStorage === "true") {
      setCameraStarted(true)
      setupCamera()
    }
    fetchEventos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const cameras = devices.filter((device) => device.kind === "videoinput")
        setCamerasAvailable(cameras.length > 0)
      })
      .catch((error) => {
        console.error("Error al enumerar dispositivos:", error)
        setCamerasAvailable(false)
      })
  }, [])

  const toggleCamera = () => {
    if (cameraStarted) {
      if (videoRef.current && "srcObject" in videoRef.current) {
        const stream = videoRef.current.srcObject as MediaStream
        if (stream) {
          const tracks = stream.getTracks()
          tracks.forEach((track) => track.stop())
        }
      }
      setCameraStarted(false)
      localStorage.setItem("cameraStarted", "false")
    } else {
      setupCamera()
    }
  }

  const setupCamera = async () => {
    requestCameraPermission()
    setCameraStarted(true)
  }

  useEffect(() => {
    if (cameraStarted) {
      requestAnimationFrame(handleScan)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraStarted])

  useEffect(() => {
    const video = videoRef.current
    return () => {
      if (cameraStarted && video) {
        if ("srcObject" in video) {
          const stream = video.srcObject as MediaStream
          const tracks = stream.getTracks()
          tracks.forEach((track) => track.stop())
        }
        setCameraStarted(false)
        localStorage.setItem("cameraStarted", "false")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeModal = () => {
    toast.dismiss()
    if (cameraStarted) {
      toggleCamera()
    }
    setQrData("")
    setShowMessage(false)
    setTarjetasPorIngresar("")
    setObservaciones("")
    setMessageText("")
    setMostrarMesa(false)
  }

  useEffect(() => {
    if (autoRestartCamera) {
      setupCamera()
      setAutoRestartCamera(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRestartCamera])

  const requestCameraPermission = async () => {
    try {
      const constraints = {
        video: {
          facingMode: "environment",
        },
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints)

      if (videoRef.current) {
        videoRef.current.srcObject = stream

        videoRef.current.addEventListener("loadedmetadata", () => {
          if (videoRef.current) {
            const width = videoRef.current.videoWidth
            const height = videoRef.current.videoHeight
            if (width > 0 && height > 0) {
              videoRef.current.play().then(() => {
                localStorage.setItem("cameraStarted", "true")
              })
            } else {
              console.error("Las dimensiones del video no son válidas.")
              setShowNoCameraAccessMessage(true)
            }
          }
        })
      }
    } catch (error) {
      console.error("Error al acceder a la cámara:", error)
      setShowNoCameraAccessMessage(true)
    }
  }

  const handleScan = async () => {
    if (!videoRef.current) {
      console.error("El elemento de video no está disponible.")
      return
    }

    const videoElement = videoRef.current

    await new Promise<void>((resolve) => {
      if (videoElement.videoWidth && videoElement.videoHeight) {
        resolve()
      } else {
        videoElement.onloadedmetadata = () => {
          resolve()
          videoElement.onloadedmetadata = null
        }
      }
    })

    scanQRCode(videoElement, (qrData: string) => {
      const scannedData = qrData.split("-")
      const rawIdentifier = scannedData[0]
      const clave = scannedData[1]
      const esQRmenor = scannedData[2]
      const idBased = rawIdentifier.startsWith("i")
      const identifier = idBased ? rawIdentifier.substring(1) : rawIdentifier
      setDni(identifier)
      setClave(clave)
      setEsMenor(esQRmenor)
      setIsIdBased(idBased)
      verificarIngreso(identifier, clave, esQRmenor, 0, idBased)
    })
  }

  // Función para manejar el descuento de tarjetas
  const handleDescontarTarjetas = async (cantidad: number) => {
    if (cantidad > 0 && dni && clave) {
      await verificarIngreso(dni, clave, esMenor, cantidad, isIdBased)
    }
  }

  const verificarIngreso = async (
    identifier: string,
    clave: string,
    esQRmenor: string,
    tarjetasADescontar: number,
    idBased: boolean = false
  ) => {
    setLoading(true)
    try {
      const token = await getAccessTokenSilently()
      let response = new Response()
      const body = idBased
        ? { id: parseInt(identifier), clave, tarjetasADescontar }
        : { dni: identifier, clave, tarjetasADescontar }
      if (esQRmenor === "V") {
        response = await fetch(idBased ? API_URL_MENOR_ID : API_URL_MENOR, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        })
      } else if (esQRmenor === "B") {
        response = await fetch(idBased ? API_URL_BAILE_ID : API_URL_BAILE, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        })
      } else {
        response = await fetch(idBased ? API_URL_ID : API_URL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        })
      }

      if (response.status === 200) {
        if (tarjetasADescontar > 0) {
          toast.success(
            tarjetasADescontar === 1
              ? `Se descontó ${tarjetasADescontar} tarjeta`
              : `Se descontaron ${tarjetasADescontar} tarjetas`,
            {
              position: "top-center",
              autoClose: false,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
            }
          )
        }

        const responseData = await response.json()
        const dniLine = responseData.dni != null ? `\nDNI: ${responseData.dni}` : ""
        setMessageText(
          `${capitalizeNombresPropios(responseData.nombre)} ${capitalizeNombresPropios(responseData.apellido)}${dniLine}\nTarjetas pagadas: ${responseData.tarjetasPagadas}\n`
        )
        setTarjetasPorIngresar(`${responseData.tarjetasPorIngresar}`)
        setObservaciones(`${responseData.observaciones}`)
        setMessageVariant("success")
        setMostrarMesa(esQRmenor !== "B")
      } else if (response.status === 418) {
        const responseData = await response.json()
        setObservaciones(`${responseData.observaciones}`)
        const dniLine418 = responseData.dni != null ? `\nDNI: ${responseData.dni}` : ""
        setMessageText(
          `${responseData.message}\n${capitalizeNombresPropios(responseData.nombre)} ${capitalizeNombresPropios(responseData.apellido)}${dniLine418}\n`
        )
        setMessageVariant("danger")
        setMostrarMesa(esQRmenor !== "B")
      } else {
        setMessageText("Ingreso Denegado.")
        setMessageVariant("danger")
      }
      setShowMessage(true)
    } catch (error) {
      // Si ocurre un error, mostrar notificación de error
      toast.error(
        "Error al verificar o descontar tarjetas.",
        {
          position: "top-center",
          autoClose: false,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      )
      console.error("Error al verificar el ingreso:", error)
    } finally {
      setLoading(false)
    }
  }
  const handleNewAction = () => {
    setShowEventModal(true) // Abre el modal al hacer clic en el botón
  }

  const handleCloseModal = () => {
    setShowEventModal(false) // Cierra el modal
  }

  const handleSelectEvent = (selectedEventId: number) => {
    setEventId(selectedEventId) // Guarda el eventId seleccionado
    handleCloseModal() // Cierra el modal
  }

  const handleShowConteoModal = () => {
    setShowConteoModal(true)
  }

  const handleCloseConteoModal = () => {
    setShowConteoModal(false)
  }

  if (loading) return <Spinner />

  return (
    <>
      <div className="container mt-5">
        <div className="d-flex flex-column align-items-center">
          <h1
            className="display-4 text-center mb-4"
            style={{ marginTop: "40px" }}
          >
            Escanea el código QR
          </h1>
          {showNoCameraAccessMessage && (
            <div className="alert alert-danger">
              No se ha concedido acceso a la cámara. Por favor, otorgue acceso y
              recargue la página.
            </div>
          )}
          {!camerasAvailable && (
            <div className="alert alert-danger">
              No se detectaron cámaras disponibles. Asegúrese de que haya una
              cámara conectada y recargue la página.
            </div>
          )}
          {cameraStarted ? (
            <div className="embed-responsive embed-responsive-4by3">
              <video
                ref={videoRef}
                className="embed-responsive-item"
                width={"500px"}
              ></video>
            </div>
          ) : (
            <p className="text-info fs-4 text-center">
              Inicie la cámara para empezar
            </p>
          )}
          {qrData && (
            <div className="text-center mt-4">
              <QRCodeSVG value={qrData} size={256} />
            </div>
          )}
          {showMessage && (
            <QRMessage
              message={messageText}
              variant={messageVariant}
              mostrarMesa={mostrarMesa}
              tarjetasPorIngresar={tarjetasPorIngresar}
              observaciones={observaciones}
              onClose={closeModal}
              onDescontarTarjetas={handleDescontarTarjetas}
              esBaile={esMenor === "B"}
            />
          )}
        </div>
        {/* Footer para móvil */}
        <div className="footer d-none d-sm-block">
          <button
            className="btn btn-secondary footer-button me-3"
            onClick={handleNewAction}
          >
            Evento
          </button>
          <button
            className="btn btn-primary footer-button"
            onClick={toggleCamera}
            disabled={!camerasAvailable}
          >
            <i className="bi bi-qr-code-scan"></i>{" "}
          </button>
          <button
            className="btn btn-secondary footer-button ms-3"
            onClick={handleShowConteoModal}
          >
            Conteo
          </button>
        </div>
        <div className="footer-mobile d-block d-sm-none">
          <button
            className="btn btn-secondary footer-button me-3"
            onClick={handleNewAction}
          >
            Evento
          </button>
          <button
            className="btn btn-primary footer-button"
            onClick={toggleCamera}
            disabled={!camerasAvailable}
          >
            <i className="bi bi-qr-code-scan"></i>{" "}
          </button>
          <button
            className="btn btn-secondary footer-button ms-3"
            onClick={handleShowConteoModal}
          >
            Conteo
          </button>
        </div>
        {/* Modal para seleccionar eventos */}
        <SelectorEventosModal
          show={showEventModal}
          handleClose={handleCloseModal}
          handleSelectEvent={handleSelectEvent}
        />
        {/* Modal de Conteo de Ingresos */}
        <ConteoIngresos
          show={showConteoModal}
          handleClose={handleCloseConteoModal}
          idEvento={eventId}
        />
      </div>
    </>
  )
}

export default EmpleadoPage
